/**
 * RF Dropdown System
 *
 * Lightweight vanilla JS for dropdown menus. No Bootstrap, no Popper.js.
 * Ported from ~/seeme/static/js/dropdowns.js — same battle-tested behavior.
 *
 * Usage — add data-rf-dropdown to any trigger inside a .rf-dropdown:
 *   <div class="rf-dropdown">
 *       <button data-rf-dropdown>Toggle</button>
 *       <div class="rf-dropdown-menu">
 *           <a class="rf-dropdown-item" href="#">Action</a>
 *       </div>
 *   </div>
 *
 * Behavior:
 * - Click trigger toggles .show on .rf-dropdown-menu
 * - Closes on: click outside, Escape, clicking an .rf-dropdown-item
 * - Auto-flips up when near viewport bottom
 * - Auto-right-aligns when overflowing viewport right edge
 * - Resets positioning on close
 *
 * Portal Mode (for table cells with overflow:hidden):
 * - Add .rf-dropdown-menu-popout or data-rf-dropdown-portal to the trigger
 * - Menu is moved to document.body with fixed positioning
 * - Repositions on scroll/resize to track the trigger
 *
 * Panel Switching:
 * - Add [data-show-panel="name"] to items that switch panels
 * - Add [data-menu-panel="name"] to panel containers within the menu
 * - Panel named "main" is shown by default on open
 *
 * JS only manages: .show, .rf-dropdown-menu--up, and inline positioning styles.
 * All other classes (rf-dropdown-menu-end, rf-dropdown-form, etc.) are read-only config.
 */
(function () {
    'use strict';

    // Track portaled menus — store original position info for restoration
    var portaledMenus = new Map();

    function resetPanels(menu) {
        var panels = menu.querySelectorAll('[data-menu-panel]');
        if (!panels.length) return;
        panels.forEach(function (p) {
            p.style.display = p.getAttribute('data-menu-panel') === 'main' ? '' : 'none';
        });
    }

    function repositionPortal(portalInfo) {
        var menu = portalInfo.menu;
        var trigger = portalInfo.trigger;
        if (!trigger || !menu) return;

        var triggerRect = trigger.getBoundingClientRect();
        var menuRect = menu.getBoundingClientRect();
        var menuWidth = menuRect.width;
        var menuHeight = menuRect.height;
        var MARGIN = 8;

        var top = triggerRect.bottom + 4;
        var left = triggerRect.left;

        // Flip up if not enough room below
        if (top + menuHeight > window.innerHeight - MARGIN) {
            top = triggerRect.top - menuHeight - 4;
        }
        top = Math.max(MARGIN, top);

        // Right-align if overflowing viewport right edge
        if (left + menuWidth > window.innerWidth - MARGIN) {
            left = triggerRect.right - menuWidth;
        }
        left = Math.max(MARGIN, Math.min(left, window.innerWidth - menuWidth - MARGIN));

        menu.style.top = top + 'px';
        menu.style.left = left + 'px';
    }

    function repositionAllPortals() {
        portaledMenus.forEach(function (portalInfo) {
            repositionPortal(portalInfo);
        });
    }

    function restorePortal(dropdown) {
        if (!portaledMenus.has(dropdown)) return;
        var info = portaledMenus.get(dropdown);
        var menu = info.menu;

        // Reset all portal inline styles
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.right = '';
        menu.style.bottom = '';
        menu.style.width = '';
        menu.style.zIndex = '';
        menu.style.visibility = '';

        // Move back to original position
        if (info.originalNextSibling && info.originalNextSibling.parentNode === info.originalParent) {
            info.originalParent.insertBefore(menu, info.originalNextSibling);
        } else {
            info.originalParent.appendChild(menu);
        }
        portaledMenus.delete(dropdown);
    }

    function closeMenu(menu, dropdown) {
        menu.classList.remove('show', 'rf-dropdown-menu--up');
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.right = '';
        menu.style.bottom = '';
        menu.style.width = '';
        resetPanels(menu);

        if (dropdown) {
            dropdown.classList.remove('show');
            restorePortal(dropdown);
        }
    }

    function closeAll(except) {
        document.querySelectorAll('.rf-dropdown-menu.show').forEach(function (menu) {
            if (menu === except) return;

            var dropdown = menu.closest('.rf-dropdown');
            if (!dropdown) {
                // Menu may be portaled — find the dropdown that owns it
                portaledMenus.forEach(function (portalInfo, dropdownKey) {
                    if (portalInfo.menu === menu) dropdown = dropdownKey;
                });
            }
            closeMenu(menu, dropdown);
        });
    }

    // --- Toggle on trigger click ---
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-rf-dropdown]');
        if (!trigger) return;

        e.preventDefault();
        e.stopPropagation();

        var dropdown = trigger.closest('.rf-dropdown');
        if (!dropdown) return;

        // Check if menu is portaled first, otherwise look in dropdown
        var menu = dropdown.querySelector('.rf-dropdown-menu');
        if (!menu && portaledMenus.has(dropdown)) {
            menu = portaledMenus.get(dropdown).menu;
        }
        if (!menu) return;

        closeAll(menu);
        var isOpening = !menu.classList.contains('show');
        var isPopout = menu.classList.contains('rf-dropdown-menu-popout') ||
                       trigger.hasAttribute('data-rf-dropdown-portal');

        if (!isOpening) {
            closeMenu(menu, dropdown);
            return;
        }

        resetPanels(menu);
        menu.classList.remove('rf-dropdown-menu--up');

        // Shared positioning variables (used by all branches)
        var triggerRect, menuRect, menuWidth, menuHeight, hasWidthClass, rect;
        var MARGIN = 8;
        var top, left;

        if (isPopout) {
            // Portal mode: move to body to escape overflow:hidden
            var originalParent = menu.parentNode;
            var originalNextSibling = menu.nextSibling;

            // Measure pattern: portal hidden at (0,0), measure, position, reveal
            menu.style.position = 'fixed';
            menu.style.zIndex = '10000';
            menu.style.visibility = 'hidden';
            menu.style.top = '0';
            menu.style.left = '0';
            menu.style.right = 'auto';
            menu.style.bottom = 'auto';
            menu.style.width = '';

            document.body.appendChild(menu);
            menu.classList.add('show');

            portaledMenus.set(dropdown, {
                menu: menu,
                trigger: trigger,
                originalParent: originalParent,
                originalNextSibling: originalNextSibling
            });

            triggerRect = trigger.getBoundingClientRect();
            menuRect = menu.getBoundingClientRect();
            menuWidth = menuRect.width;
            menuHeight = menuRect.height;

            // Lock width so content doesn't reflow during repositions
            hasWidthClass = /rf-dropdown-menu--(sm|md|lg)/.test(menu.className);
            if (!hasWidthClass) menu.style.width = menuWidth + 'px';

            top = triggerRect.bottom + 4;
            left = triggerRect.left;

            if (top + menuHeight > window.innerHeight - MARGIN) {
                top = triggerRect.top - menuHeight - 4;
            }
            top = Math.max(MARGIN, top);

            if (left + menuWidth > window.innerWidth - MARGIN) {
                left = triggerRect.right - menuWidth;
            }
            left = Math.max(MARGIN, Math.min(left, window.innerWidth - menuWidth - MARGIN));

            menu.style.top = top + 'px';
            menu.style.left = left + 'px';
            menu.style.visibility = '';
            dropdown.classList.add('show');

        } else {
            // Standard mode: position relative to parent
            menu.classList.add('show');
            dropdown.classList.add('show');

            // Check if inside a scroll container (table-responsive, etc.)
            var scrollParent = trigger.closest('.table-responsive') ||
                               trigger.closest('[style*="overflow"]');

            if (scrollParent) {
                // Use fixed positioning to escape overflow
                triggerRect = trigger.getBoundingClientRect();
                hasWidthClass = /rf-dropdown-menu--(sm|md|lg)/.test(menu.className);
                menuWidth = menu.offsetWidth;
                menu.style.position = 'fixed';
                if (!hasWidthClass) menu.style.width = menuWidth + 'px';

                top = triggerRect.bottom + 4;
                left = triggerRect.left;

                if (top + menu.offsetHeight > window.innerHeight - MARGIN) {
                    top = triggerRect.top - menu.offsetHeight - 4;
                }
                top = Math.max(MARGIN, top);

                if (left + menuWidth > window.innerWidth - MARGIN) {
                    left = triggerRect.right - menuWidth;
                }
                left = Math.max(MARGIN, Math.min(left, window.innerWidth - menuWidth - MARGIN));

                menu.style.top = top + 'px';
                menu.style.left = left + 'px';
                menu.style.bottom = 'auto';
            } else {
                rect = menu.getBoundingClientRect();
                triggerRect = trigger.getBoundingClientRect();

                // Flip up if not enough room below
                if (rect.bottom > window.innerHeight && triggerRect.top > window.innerHeight - triggerRect.bottom) {
                    menu.classList.add('rf-dropdown-menu--up');
                }

                // Auto right-align if overflowing viewport right edge
                if (!menu.classList.contains('rf-dropdown-menu-end') && rect.right > window.innerWidth) {
                    menu.style.right = '0';
                    menu.style.left = 'auto';
                }
            }
        }
    });

    // --- Panel switching ---
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-show-panel]');
        if (!trigger) return;
        e.preventDefault();
        var menu = trigger.closest('.rf-dropdown-menu');
        if (!menu) return;
        var target = trigger.getAttribute('data-show-panel');
        menu.querySelectorAll('[data-menu-panel]').forEach(function (p) {
            p.style.display = p.getAttribute('data-menu-panel') === target ? '' : 'none';
        });
        menu.style.width = '';
    });

    // --- Close on item click ---
    document.addEventListener('click', function (e) {
        var item = e.target.closest('.rf-dropdown-item');
        if (!item) return;
        if (item.hasAttribute('data-rf-keep-open') || item.hasAttribute('data-show-panel')) return;

        // Don't auto-close form/confirm panels — let submit/cancel handle it
        var menu = item.closest('.rf-dropdown-menu');
        if (menu && (menu.classList.contains('rf-dropdown-form') || menu.classList.contains('rf-dropdown-confirm'))) return;

        closeAll();
    });

    // --- Close on click outside ---
    document.addEventListener('click', function (e) {
        if (e.target.closest('[data-rf-dropdown]')) return;
        if (e.target.closest('.rf-dropdown-menu.show')) return;

        // Check portaled menus (outside DOM tree)
        var clickedPortaled = false;
        portaledMenus.forEach(function (info) {
            if (info.menu.contains(e.target)) clickedPortaled = true;
        });
        if (clickedPortaled) return;

        closeAll();
    });

    // --- Close on cancel/close button click ---
    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.js-rf-dropdown-close');
        if (!btn) return;
        closeAll();
    });

    // --- Close on Escape ---
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAll();
    });

    // --- Reposition portaled menus on scroll/resize ---
    window.addEventListener('scroll', function () {
        if (portaledMenus.size > 0) repositionAllPortals();
    }, true);

    window.addEventListener('resize', function () {
        if (portaledMenus.size > 0) repositionAllPortals();
    });
})();
